/**
 * Rebuild `agent.messages` from persisted history for the AG-UI chat panel.
 *
 * Extracted from AgUiChatPanel so the matching logic — the part most likely to
 * be wrong — can be unit tested without mounting the component (importing the
 * panel drags in React, Cloudscape and CopilotKit's CSS). Same reason
 * messageWindowMiddleware lives on its own.
 */
import type { SessionMessage, SettledGate } from '../../../client/GatewayClient';
import { HITL_TOOL_NAMES } from './hitlToolNames';

/** An `agent.messages` entry we hand to `agent.setMessages`. */
type ReplayMessage =
  | { id: string; role: 'user' | 'assistant'; content: string }
  | {
      id: string;
      role: 'assistant';
      content: string;
      toolCalls: Array<{
        id: string;
        type: 'function';
        function: { name: string; arguments: string };
      }>;
    }
  | { id: string; role: 'tool'; toolCallId: string; content: string };

/**
 * Stand-in result for a restored non-gate tool call.
 *
 * DynamoDB stores no tool results at all — `MessageType`
 * (`models/conversation.py:15-19`) has only user_query / agent_final_response /
 * agent_thought / tool_use, and `TOOL_CALL_RESULT` is never even emitted for
 * frontend tools. So a restored tool call has two possible honest states:
 * paired with a fabricated result, or left unpaired forever.
 *
 * Unpaired is worse: CopilotKit resolves status by looking for a `role:'tool'`
 * message with the matching id, so an unpaired call renders as *still running*
 * for the rest of the session — a spinner on a tool that finished hours ago.
 * Hence a marker, deliberately self-describing rather than empty or plausible,
 * so anything that ever does surface tool results shows "restored" instead of
 * inventing an outcome. The trace row does not render results today.
 *
 * ⚠️ It must never be the string `"Forwarded to client"`:
 * `isFrontendPlaceholderResult` in `@copilotkit/core` (`:2088-2091`) deletes a
 * result with exactly that content and RE-RUNS the tool handler.
 */
export const RESTORED_TOOL_RESULT = '{"restored":true}';

/** A tool call reconstructed from one persisted `tool_use` row. */
export interface PlannedToolCall {
  /** The AG-UI `toolCallId` — display key, pairing key, and resume address. */
  toolCallId: string;
  name: string;
  args: unknown;
  result: string;
}

/** Sentinel `redact_sensitive_keys` (`components/common/redact.py:135`) leaves
 *  behind, so a masked value is never read as a content mismatch. */
const REDACTED = '[REDACTED]';

/** Keys the Runtime writes when it could not store the real arguments: the size
 *  sentinel and the unserializable fallback from `truncate_tool_input`
 *  (`components/persistence.py:50-59`), and `_raw` for an args stream cut
 *  mid-call (`components/ag_ui_chat/persistence.py:466-474`). Any of them means
 *  the row's `tool_input` is not the arguments and cannot be compared to them. */
const UNUSABLE_TOOL_INPUT_KEYS = ['_truncated', '_raw', '_unserializable'];

/** Whether two argument values agree, treating a redacted or absent value as no
 *  evidence either way rather than as a difference. */
function valuesAgree(rowValue: unknown, gateValue: unknown): boolean {
  if (rowValue === REDACTED || gateValue === REDACTED) return true;
  if (rowValue === undefined || gateValue === undefined) return true;
  if (Array.isArray(rowValue) && Array.isArray(gateValue)) {
    if (rowValue.length !== gateValue.length) return false;
    return rowValue.every((v, i) => valuesAgree(v, gateValue[i]));
  }
  if (isPlainObject(rowValue) && isPlainObject(gateValue)) {
    // Compared on shared keys only, for the same reason as the top level: a key
    // one side does not carry is not a contradiction.
    return Object.keys(rowValue)
      .filter((k) => k in gateValue)
      .every((k) => valuesAgree(rowValue[k], gateValue[k]));
  }
  return Object.is(rowValue, gateValue);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Does this row's persisted `tool_input` describe the same call as this verdict's
 * `arguments`?
 *
 * `'unverifiable'` is a third answer, not a soft `'disagree'`: DynamoDB may hold
 * no `tool_input` for the row (dropped when None), a size sentinel instead of the
 * arguments, or a dict whose keys the verdict does not carry. Distinguishing it
 * from a real contradiction is the whole point — a contradiction is evidence the
 * pairing is wrong, while no evidence only means we must fall back on position.
 */
function compareArgs(
  rowInput: SessionMessage['tool_input'],
  gateArgs: SettledGate['arguments'],
): 'agree' | 'disagree' | 'unverifiable' {
  if (!isPlainObject(rowInput) || !isPlainObject(gateArgs)) return 'unverifiable';
  if (UNUSABLE_TOOL_INPUT_KEYS.some((k) => k in rowInput)) return 'unverifiable';
  const shared = Object.keys(rowInput).filter(
    (k) => k in gateArgs && rowInput[k] !== REDACTED && gateArgs[k] !== REDACTED,
  );
  if (shared.length === 0) return 'unverifiable';
  return shared.every((k) => valuesAgree(rowInput[k], gateArgs[k])) ? 'agree' : 'disagree';
}

export interface PlanOptions {
  /**
   * True only when `rows` covers the conversation from its FIRST message with
   * nothing trimmed off the end. Gates positional pairing — see `planToolCalls`.
   */
  historyIsComplete?: boolean;
}

/**
 * Decide, for every persisted `tool_use` row, what tool call it becomes.
 *
 * **The single source of truth for that decision**, keyed by the row's
 * `message_id`, and read by `buildReplayMessages` (which builds `agent.messages`,
 * the source of each rendered element's CONTENT and of WHERE on screen it
 * appears). It was once read by a second, separate display projection too; when
 * the two disagreed we got a card with no position, then cards with no card — the
 * same bug twice. One map, one decision, and it stays exported and tested
 * separately because it is the part most likely to be wrong.
 *
 * Two kinds of row, two rules:
 *
 * **Gate rows** (`HITL_TOOL_NAMES`) must be matched to a verdict from the Runtime.
 * Three rules, in order, and which one applies depends on how old the row is:
 *
 *   0. **The row's `tool_call_id`**, when it has one — the real key, exact and
 *      order-independent. Written by the Runtime since 2026-09-28. A row that has
 *      an id and matches no verdict is a definite no-match and is omitted; it does
 *      NOT fall through to the guesses below.
 *   1. Otherwise the row's `tool_input` is compared against each unconsumed
 *      verdict's `arguments` (`compareArgs`), and the first that agrees is taken.
 *   2. If nothing can be compared — no `tool_input`, a size sentinel, no shared
 *      keys — the Nth row of a name pairs with the Nth verdict of that name,
 *      **but only when `historyIsComplete`**.
 *   3. Anything else is omitted.
 *
 * Steps 1-2 exist only for rows written before step 0 was possible, and step 2 is
 * fenced because ⚠️ **position alone is not sound**. Both lists are in conversation
 * order, but they do not cover the same span: the caller loads DynamoDB one
 * 100-message page at a time and trims an incomplete trailing turn, while
 * `getGateHistory` returns the verdicts of the WHOLE conversation. Pair the 1st row
 * of a later page with the 1st verdict of the conversation and the card renders
 * another gate's arguments and another gate's verdict — "已批准" on a segment that
 * was rejected. **A card showing the WRONG verdict is far worse than a missing
 * card**, which is what every branch here is arranged around, and a missing card is
 * the documented degradation for an old, long conversation.
 *
 * Steps 1-3 can be deleted once no thread anyone will reopen predates the column.
 *
 * **Every other row** always yields a call, with a synthesised id (the row's
 * `message_id` is unique, so the id is too) and the row's own `tool_input` as
 * arguments. Note `tool_input` is truncated above 30 KB by
 * `truncate_tool_input` (`components/persistence.py:54-60`), so a restored trace
 * row can show a 1 KB preview where the live one showed the full arguments.
 */
export function planToolCalls(
  rows: SessionMessage[],
  settledGates: SettledGate[] = [],
  opts: PlanOptions = {},
): Map<string, PlannedToolCall> {
  const remaining = new Map<string, SettledGate[]>();
  for (const gate of settledGates) {
    // The Runtime already filters these out; dropped here too, because the cost
    // of letting one through is re-executing a gate the operator already
    // answered. See RESTORED_TOOL_RESULT.
    if (gate.result?.trim() === 'Forwarded to client') continue;
    const bucket = remaining.get(gate.tool_name);
    if (bucket) bucket.push(gate);
    else remaining.set(gate.tool_name, [gate]);
  }

  // Pass 0 — reserve every verdict that a row CLAIMS BY ID, before the heuristics can
  // consume it. Without this the passes fight across a deploy boundary: an older
  // unkeyed row processed first takes `bucket.shift()`, which may be the verdict a
  // later keyed row owns, so the old row renders another gate's verdict — the exact
  // "已批准 on a rejected segment" this whole function is arranged to prevent — and the
  // keyed row finds nothing and is dropped.
  const claimedByKey = new Map<string, SettledGate>();
  for (const row of rows) {
    if (row.message_type !== 'tool_use') continue;
    if (!HITL_TOOL_NAMES.has(row.message_content)) continue;
    const rowCallId = row.tool_call_id;
    if (!rowCallId) continue;
    const bucket = remaining.get(row.message_content);
    if (!bucket) continue;
    const keyIdx = bucket.findIndex((g) => g.tool_call_id === rowCallId);
    if (keyIdx >= 0) claimedByKey.set(row.message_id, bucket.splice(keyIdx, 1)[0]);
  }

  const planned = new Map<string, PlannedToolCall>();
  for (const row of rows) {
    if (row.message_type !== 'tool_use') continue;
    const name = row.message_content;
    if (HITL_TOOL_NAMES.has(name)) {
      const rowCallId = row.tool_call_id;
      // Step 0 — the real key, reserved in pass 0 above. Exact, order-independent,
      // and immune to every way the two lists can cover different spans of the
      // conversation. A row that HAS an id and matched nothing is a definite
      // no-match, not a reason to start guessing: it falls to the omit-and-warn
      // below rather than to the heuristics.
      let gate: SettledGate | undefined = claimedByKey.get(row.message_id);
      const bucket = remaining.get(name);
      let verdicts: Array<'agree' | 'disagree' | 'unverifiable'> = [];
      if (!gate && !rowCallId && bucket && bucket.length > 0) {
        verdicts = bucket.map((g) => compareArgs(row.tool_input, g.arguments));
        const agreeIdx = verdicts.indexOf('agree');
        if (agreeIdx >= 0) {
          gate = bucket.splice(agreeIdx, 1)[0];
        } else if (verdicts.every((v) => v === 'unverifiable') && opts.historyIsComplete) {
          gate = bucket.shift();
        }
      }
      if (!gate) {
        // eslint-disable-next-line no-console
        console.warn(
          '[agui/replay] no verdict safely pairs with gate row %s (%s) — omitting its card. %s',
          row.message_id,
          name,
          rowCallId
            ? `tool_call_id=${rowCallId} not among the verdicts`
            : `no tool_call_id; args comparison: ${verdicts.join(',') || '(no verdicts)'}`,
        );
        continue;
      }
      planned.set(row.message_id, {
        toolCallId: gate.tool_call_id,
        name,
        args: gate.arguments ?? {},
        result: gate.result,
      });
    } else {
      planned.set(row.message_id, {
        toolCallId: `${row.message_id}-tc`,
        name,
        args: row.tool_input ?? {},
        result: RESTORED_TOOL_RESULT,
      });
    }
  }
  return planned;
}

/**
 * Rebuild `agent.messages` from DynamoDB history, splicing back the tool calls
 * so CopilotKit's registered renderers — the approval panels and the
 * wildcard trace row — work on restored history exactly as they do live.
 *
 * Each planned call becomes the pair CopilotKit needs to resolve a tool call to
 * `Complete`: an assistant message carrying `toolCalls[]`, then the matching
 * `role:'tool'` message. Injecting these does NOT re-execute the handler —
 * `processAgentResult` only scans messages produced by the current run
 * (`newMessages` excludes everything present before it started), and a paired
 * result short-circuits execution anyway.
 *
 * These extra messages do not reach the Runtime: `messageWindowMiddleware`
 * trims everything before the latest user message, and restored history is by
 * definition before it.
 */
export function buildReplayMessages(
  rows: SessionMessage[],
  settledGates: SettledGate[],
  opts: PlanOptions = {},
): ReplayMessage[] {
  const planned = planToolCalls(rows, settledGates, opts);

  const out: ReplayMessage[] = [];
  for (const row of rows) {
    if (row.message_type === 'user_query' || row.message_type === 'agent_final_response') {
      out.push({
        id: row.message_id,
        role: row.message_type === 'user_query' ? 'user' : 'assistant',
        content: row.message_content,
      });
      continue;
    }
    const call = planned.get(row.message_id);
    if (!call) continue;
    out.push({
      id: `${row.message_id}-call`,
      role: 'assistant',
      content: '',
      toolCalls: [
        {
          id: call.toolCallId,
          type: 'function',
          function: { name: call.name, arguments: JSON.stringify(call.args ?? {}) },
        },
      ],
    });
    out.push({
      id: `${row.message_id}-result`,
      role: 'tool',
      toolCallId: call.toolCallId,
      content: call.result,
    });
  }
  return out;
}
